<template>
  <div class="services-view">
    <ServicesTopbar
        ref="topbarRef"
        :park="park"
        :loading="loading"
        :alerts="alerts"
        v-model="selectedPeriod"
        @register-service="onRegisterService"
        @distribute="onDistribute"
        @open-notifications="onOpenNotifications"
      />

    <div v-if="loading || !metrics" class="services-loading">
      <span class="mdi mdi-loading services-loading__spinner"></span>
      <span class="services-loading__text">Cargando servicios...</span>
    </div>

    <div v-else class="services-view__body">
      <ServicesMetricsRow
        v-if="metrics"
        :metrics="metrics"
        :period="selectedPeriod"
        :park-id="park.id"
        @scroll-to-list="onScrollToList"
        @scroll-to-distribution="onScrollToDistribution"
        @register-service="onRegisterService"
      />
      <div class="services-view__columns">
        <ServicesList
          v-if="selectedPeriod"
          ref="listRef"
          :park-id="park.id"
          :period="selectedPeriod"
          :highlighted-service-ids="highlightedServiceIds"
          @changed="loadMetrics"
          @open-schemes="showSchemesPanel = true"
        />

        <ServiceSchemesPanel
          v-if="showSchemesPanel"
          :park-id="park.id"
          :period="selectedPeriod"
          @close="showSchemesPanel = false"
          @saved="onSchemesSaved"
        />
        <ServicesDistribution
          v-else-if="selectedPeriod"
          ref="distributionRef"
          :park-id="park.id"
          :period="selectedPeriod"
          @hover-services="onHoverServices"
        />
      </div>
      <ServiceDistributionConfirmModal
          v-if="showConfirmModal"
          :park-id="park.id"
          :period="selectedPeriod"
          @close="showConfirmModal = false"
          @confirmed="onDistributionConfirmed"
          @view-distribution="onViewFullDistribution"
        />
    </div>
  </div>

</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ServicesTopbar from '@/components/Servicios/ServicesTopbar.vue'
import ServicesMetricsRow from '@/components/Servicios/ServicesMetricsRow.vue'
import { getParks, getParkById } from '@/services/parks'
import { getParkAlerts } from '@/services/alerts'
import { getServicesMetrics } from '@/services/services'
import ServicesList from '@/components/Servicios/ServicesList.vue'
import ServicesDistribution from '@/components/Servicios/ServicesDistribution.vue'
import ServiceSchemesPanel from '@/components/Servicios/ServiceSchemesPanel.vue'
import ServiceDistributionConfirmModal from '@/components/Servicios/ServiceDistributionConfirmModal.vue'

const showConfirmModal = ref(false)

function onDistribute() {
  showConfirmModal.value = true
}

async function onDistributionConfirmed() {
  showConfirmModal.value = false
  await loadMetrics()
  await listRef.value?.reload()
}

function onViewFullDistribution() {
  showConfirmModal.value = false
  onScrollToDistribution()
}

const showSchemesPanel = ref(false)
const listRef = ref(null)

const distributionRef = ref(null)
const highlightedServiceIds = ref([])

function onHoverServices(ids) {
  highlightedServiceIds.value = ids
}

async function loadMetrics() {
  if (!park.value || !selectedPeriod.value) return
  metrics.value = await getServicesMetrics(park.value.id, selectedPeriod.value)
  distributionRef.value?.reload()
  topbarRef.value?.reloadSummary()
}

async function onSchemesSaved() {
  await listRef.value?.reload()
  await loadMetrics()
}

const park = ref(null)
const loading = ref(true)
const alerts = ref([])
const selectedPeriod = ref('')
const metrics = ref(null)
const topbarRef = ref(null)

const route = useRoute()
const router = useRouter()

onMounted(async () => {
  try {
    const parks = await getParks()
    if (parks[0]) {
      const [fullPark, alertsData] = await Promise.all([getParkById(parks[0].id), getParkAlerts(parks[0].id)])
      park.value = fullPark
      alerts.value = alertsData
      if (route.query.period) {
        selectedPeriod.value = route.query.period
        router.replace({ path: '/Servicios', query: {} })
      }
    }
  } finally {
    loading.value = false
  }
})

watch(selectedPeriod, loadMetrics)



async function onRegisterService() {
  await listRef.value?.addService()
  await loadMetrics()
}



function onScrollToList() {
  // TODO: hacer scroll + highlight cuando exista la lista de servicios
  console.log('Scroll a lista de servicios')
}

function onScrollToDistribution() {
  // TODO: hacer scroll + highlight cuando exista la vista de distribución
  console.log('Scroll a distribución por inquilino')
}

function onOpenNotifications() {
  console.log('Abrir notificaciones')
}
</script>

<style scoped>
.services-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.services-view__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.services-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--text-secondary);
}

.services-loading__spinner {
  font-size: 1.8rem;
  animation: services-spin 0.8s linear infinite;
}

.services-loading__text {
  font-size: 0.85rem;
  font-family: var(--font-body);
}

@keyframes services-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.services-view__columns {
  flex: 1;
  min-height: 0;
  display: flex;
  padding-top: 1rem;
}

.panel-slide-enter-active, .panel-slide-leave-active { transition: transform 250ms ease-out, opacity 250ms ease-out; }
.panel-slide-enter-from, .panel-slide-leave-to { transform: translateX(24px); opacity: 0; }
</style>